import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  AlertCircle,
  Download,
  FileSpreadsheet,
  Loader2,
  RotateCcw,
  Search,
  ShieldCheck,
  Upload,
  X,
} from 'lucide-react';
import { Button } from '@tark/shared/components';
import {
  useHistoricalImportActions,
  useHistoricalImportBatch,
  useHistoricalImportBatches,
  useHistoricalImportMemberCandidates,
  useSquadDetail,
} from '@tark/shared/hooks';
import { getErrorMessage } from '@tark/shared/lib';
import type {
  HistoricalImportAlias,
  HistoricalImportApplyResult,
  HistoricalImportBatchDetail,
  HistoricalImportBatchSummary,
  HistoricalImportMemberCandidate,
  HistoricalImportRow,
} from '@tark/shared/types';

type ImportStep = 0 | 1 | 2 | 3;
type RowFilter = 'ALL' | 'READY' | 'ATTENTION' | 'HISTORY';

const STEPS = ['Upload', 'Map players', 'Review', 'Confirm'] as const;
const CSV_TEMPLATE = [
  'played_date,played_time,source_match_id,discipline,format,side_a_player_1,side_a_player_2,side_b_player_1,side_b_player_2,score,completion,declared_winner,note',
  '2026-08-23,19:30,summer-001,DOUBLES,SHORT_SET,Alex Morgan,Sam Lee,Jordan Patel,Chris Ray,4-2,COMPLETED,SIDE_A,',
  '2026-08-24,,summer-002,SINGLES,STANDARD_BEST_OF_THREE,Alex Morgan,,Jordan Patel,,6-4 3-6 10-7,COMPLETED,SIDE_A,Date only is accepted',
].join('\r\n');

const ACTIVE_BATCH_STATUSES = new Set(['PARSED', 'NEEDS_MAPPING', 'READY']);

const REASON_LABELS: Record<string, string> = {
  ALIAS_NOT_MAPPED: 'Map every player name to a squad member.',
  MAPPED_USER_NOT_FOUND: 'The mapped member is no longer available.',
  MAPPED_USER_NOT_READY: 'The mapped member has not completed account setup.',
  MAPPED_USER_EMAIL_NOT_VERIFIED: 'The mapped member must verify their email.',
  MAPPED_USER_MOBILE_NOT_VERIFIED: 'The mapped member must verify their mobile number.',
  MAPPED_USER_NOT_ACTIVE_SQUAD_MEMBER: 'The mapped player is not an active squad member.',
  MAPPED_USER_APPEARS_MORE_THAN_ONCE: 'The same member cannot appear on both sides of a match.',
  SOURCE_MATCH_ID_DUPLICATE: 'This match was already included in an earlier import.',
  SOURCE_MATCH_ID_CONFLICT: 'This match reference conflicts with an earlier import.',
  FILE_EXACT_DUPLICATE: 'This exact file was uploaded before.',
  SOURCE_MATCH_ID_DUPLICATE_IN_FILE: 'This match reference appears more than once in the file.',
  FORMAT_NOT_REGISTERED: 'The match format is not supported.',
  SCORE_INVALID: 'The score does not match the selected format.',
  PLAYED_DATE_INVALID: 'Use a valid match date.',
  PLAYED_TIME_INVALID: 'Use a valid match time.',
  PLAYED_TIME_IN_FUTURE: 'A match date or time is in the future.',
  PLAYER_ALIAS_REQUIRED: 'A player name is missing.',
  PLAYER_APPEARS_MORE_THAN_ONCE: 'The same player appears on both sides.',
  DISCIPLINE_NOT_SUPPORTED: 'Use SINGLES or DOUBLES.',
  SINGLES_CARDINALITY_INVALID: 'Singles matches must have one player on each side.',
  DECLARED_WINNER_INVALID: 'Use SIDE_A, SIDE_B, or TIE for the winner.',
  DECLARED_TIE_CONFLICT: 'The score does not support a tied result.',
};

function friendlyError(error: unknown): string {
  const message = getErrorMessage(error);
  if (/SQUAD_ADMIN_REQUIRED/i.test(message)) return 'Only a squad admin can import match history.';
  if (/SQUAD_HOME_TIMEZONE/i.test(message)) return 'Add a valid squad home ZIP before importing match history.';
  if (/UPLOAD_RESERVATION_EXPIRED/i.test(message)) return 'The upload window expired. Choose the file again.';
  if (/CSV_HEADERS_MISSING/i.test(message)) return 'The file is missing required columns. Download the TARK template and try again.';
  if (/CSV_HEADERS_UNKNOWN/i.test(message)) return 'The file contains unsupported columns. Use the TARK template without renaming its columns.';
  if (/CSV_TOO_MANY/i.test(message)) return 'A file can contain up to 1,000 matches.';
  if (/CSV|HISTORICAL_IMPORT/i.test(message)) return 'TARK could not read that score file. Check the template and try again.';
  return message || 'Something went wrong. Try again.';
}

function downloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = 'tark-match-history-template.csv';
  anchor.click();
  URL.revokeObjectURL(href);
}

function initialStep(detail?: HistoricalImportBatchDetail): ImportStep {
  if (!detail) return 0;
  if (detail.summary.needsMappingCount > 0) return 1;
  if (detail.summary.status === 'READY') return 2;
  return 2;
}

function formatDate(value?: string | null): string {
  if (!value) return 'Not uploaded';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function statusLabel(status: string): string {
  return {
    DRAFT: 'Awaiting upload',
    PARSED: 'Needs review',
    NEEDS_MAPPING: 'Map players',
    READY: 'Ready to import',
    APPLIED: 'Imported',
    CANCELLED: 'Cancelled',
    REJECTED: 'Not imported',
  }[status] || 'Needs review';
}

function rowStatus(row: HistoricalImportRow): { label: string; tone: string } {
  if (row.status === 'READY') return { label: 'Ready', tone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' };
  if (row.status === 'HISTORY_ONLY') return { label: 'History only', tone: 'bg-sky-500/10 text-sky-700 dark:text-sky-300' };
  if (row.status === 'NEEDS_MAPPING') return { label: 'Map players', tone: 'bg-amber-500/10 text-amber-800 dark:text-amber-300' };
  if (row.status === 'DUPLICATE') return { label: 'Already imported', tone: 'bg-slate-500/10 text-text-muted' };
  if (row.status === 'CONFLICT') return { label: 'Conflict', tone: 'bg-red-500/10 text-red-700 dark:text-red-300' };
  return { label: 'Fix needed', tone: 'bg-red-500/10 text-red-700 dark:text-red-300' };
}

function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] || 'This row needs attention before it can be imported.';
}

export default function HistoricalImportPage() {
  const { squadId } = useParams();
  const [batchId, setBatchId] = useState<string | null>(null);
  const [step, setStep] = useState<ImportStep>(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<HistoricalImportApplyResult | null>(null);
  const { data: squad, isLoading: squadLoading } = useSquadDetail(squadId);
  const adminSquadId = squad?.userRole === 'ADMIN' ? squadId : undefined;
  const batchesQuery = useHistoricalImportBatches(adminSquadId);
  const batchQuery = useHistoricalImportBatch(adminSquadId, batchId);
  const membersQuery = useHistoricalImportMemberCandidates(batchId ? adminSquadId : undefined);
  const actions = useHistoricalImportActions(squadId);
  const openedBatch = useRef<string | null>(null);
  const applyKeys = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!batchQuery.data || openedBatch.current === batchQuery.data.summary.batchId) return;
    openedBatch.current = batchQuery.data.summary.batchId;
    setStep(initialStep(batchQuery.data));
  }, [batchQuery.data]);

  const activeBatches = useMemo(
    () => (batchesQuery.data || []).filter((batch) => ACTIVE_BATCH_STATUSES.has(batch.status)),
    [batchesQuery.data]
  );

  const openBatch = (id: string) => {
    setNotice(null);
    setError(null);
    setApplyResult(null);
    setBatchId(id);
  };

  const startNew = () => {
    openedBatch.current = null;
    setBatchId(null);
    setStep(0);
    setNotice(null);
    setError(null);
    setApplyResult(null);
  };

  if (squadLoading || batchesQuery.isLoading) {
    return <CenteredLoading label="Loading match import" />;
  }

  if (!squad || squad.userRole !== 'ADMIN') {
    return (
      <div className="min-h-screen px-4 py-6 md:px-8">
        <div className="mx-auto max-w-3xl">
          <NavLink to={squadId ? `/squads/${squadId}` : '/squads'} className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text">
            <ArrowLeft className="h-4 w-4" /> Back to squad
          </NavLink>
          <div className="mt-8 border-y border-border py-10 text-center">
            <ShieldCheck className="mx-auto h-8 w-8 text-text-muted" />
            <h1 className="mt-4 text-2xl font-semibold text-text">Admin access required</h1>
            <p className="mt-2 text-sm text-text-muted">Only a squad admin can import historical scores.</p>
          </div>
        </div>
      </div>
    );
  }

  const detail = batchQuery.data;

  return (
    <div className="min-h-screen px-4 py-5 md:px-8 md:py-7">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <NavLink to={`/squads/${squad.id}`} className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text">
              <ArrowLeft className="h-4 w-4" /> {squad.name}
            </NavLink>
            <h1 className="mt-3 text-2xl font-bold text-text sm:text-3xl">Import match history</h1>
            <p className="mt-1 text-sm text-text-muted">Bring verified squad results into TARK without recreating each match.</p>
          </div>
          {detail ? (
            <Button variant="outline" size="sm" onClick={startNew} leftIcon={<RotateCcw className="h-4 w-4" />}>
              New import
            </Button>
          ) : null}
        </header>

        {detail ? <StepNavigation step={step} detail={detail} onStep={setStep} /> : null}

        {notice ? (
          <div className="mt-5 border-l-4 border-emerald-500 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200" role="status">
            {notice}
          </div>
        ) : null}
        {error ? (
          <div className="mt-5 border-l-4 border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-800 dark:text-red-200" role="alert">
            {error}
          </div>
        ) : null}

        {!detail && activeBatches.length > 0 ? (
          <DraftsBand batches={activeBatches} onOpen={openBatch} />
        ) : null}

        <main className="py-6">
          {!detail || step === 0 ? (
            <UploadStep
              isUploading={actions.isUploading}
              onUpload={async (file, sourceLabel, sourceLedgerId) => {
                setError(null);
                setNotice(null);
                try {
                  const result = await actions.upload({
                    file,
                    payload: {
                      originalFilename: file.name,
                      contentLength: file.size,
                      sourceLabel,
                      sourceLedgerId,
                    },
                  });
                  openedBatch.current = result.summary.batchId;
                  setBatchId(result.summary.batchId);
                  setStep(initialStep(result));
                  setNotice(`${result.summary.matchCount} matches were read from ${file.name}.`);
                } catch (uploadError) {
                  setError(friendlyError(uploadError));
                }
              }}
            />
          ) : null}

          {detail && step === 1 ? (
            <MappingStep
              detail={detail}
              candidates={membersQuery.data || []}
              loadingCandidates={membersQuery.isLoading}
              isSaving={actions.isMapping}
              onMap={async (alias, userId) => {
                setError(null);
                try {
                  await actions.mapAlias({ batchId: detail.summary.batchId, aliasId: alias.id, userId });
                } catch (mappingError) {
                  setError(friendlyError(mappingError));
                }
              }}
              onUnmap={async (alias) => {
                setError(null);
                try {
                  await actions.unmapAlias({ batchId: detail.summary.batchId, aliasId: alias.id });
                } catch (mappingError) {
                  setError(friendlyError(mappingError));
                }
              }}
              onContinue={() => setStep(2)}
            />
          ) : null}

          {detail && step === 2 ? (
            <ReviewStep
              detail={detail}
              isValidating={actions.isValidating}
              onValidate={async () => {
                setError(null);
                try {
                  const result = await actions.validate(detail.summary.batchId);
                  setNotice(result.summary.status === 'READY' ? 'Every match passed review.' : 'Review refreshed. Resolve the remaining items before import.');
                } catch (validationError) {
                  setError(friendlyError(validationError));
                }
              }}
              onBack={() => setStep(1)}
              onContinue={() => setStep(3)}
            />
          ) : null}

          {detail && step === 3 ? (
            <ConfirmStep
              detail={detail}
              result={applyResult}
              isApplying={actions.isApplying}
              onBack={() => setStep(2)}
              onApply={async () => {
                setError(null);
                setNotice(null);
                const id = detail.summary.batchId;
                const idempotencyKey = applyKeys.current[id]
                  || `historical-import:${id}:${crypto.randomUUID()}`;
                applyKeys.current[id] = idempotencyKey;
                try {
                  const result = await actions.apply({
                    batchId: id,
                    idempotencyKey,
                    reason: 'Squad admin reviewed player mappings and historical scores.',
                  });
                  setApplyResult(result);
                  setNotice(`${result.importedMatchCount} matches are now in TARK.`);
                } catch (applyError) {
                  setError(friendlyError(applyError));
                }
              }}
            />
          ) : null}
        </main>
      </div>
    </div>
  );
}

function CenteredLoading({ label }: { label: string }) {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Loader2 className="h-7 w-7 animate-spin text-primary" aria-label={label} />
    </div>
  );
}

function StepNavigation({ step, detail, onStep }: { step: ImportStep; detail: HistoricalImportBatchDetail; onStep: (step: ImportStep) => void }) {
  return (
    <nav className="mt-5 overflow-x-auto" aria-label="Import progress">
      <ol className="grid min-w-[35rem] grid-cols-4 border-y border-border">
        {STEPS.map((label, index) => {
          const current = step === index;
          const reached = index === 0 || detail.summary.uploadedAt || index < step;
          return (
            <li key={label}>
              <button
                type="button"
                aria-current={current ? 'step' : undefined}
                disabled={!reached}
                onClick={() => onStep(index as ImportStep)}
                className={`flex h-14 w-full items-center justify-center gap-2 border-r border-border px-3 text-sm font-semibold last:border-r-0 ${
                  current ? 'bg-primary/10 text-primary' : reached ? 'text-text hover:bg-surface-elevated/60' : 'text-text-subtle'
                }`}
              >
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${current ? 'bg-primary text-white' : 'bg-surface-elevated text-text-muted'}`}>
                  {index < step ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                {label}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function DraftsBand({ batches, onOpen }: { batches: HistoricalImportBatchSummary[]; onOpen: (batchId: string) => void }) {
  return (
    <section className="mt-6 border-y border-border py-4" aria-labelledby="draft-imports-title">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 id="draft-imports-title" className="font-semibold text-text">Continue an import</h2>
          <p className="mt-1 text-sm text-text-muted">Your mapping and review progress is saved.</p>
        </div>
        <span className="text-sm font-semibold text-primary">{batches.length} open</span>
      </div>
      <div className="mt-3 divide-y divide-border">
        {batches.slice(0, 3).map((batch) => (
          <button key={batch.batchId} type="button" onClick={() => onOpen(batch.batchId)} className="flex w-full items-center justify-between gap-4 py-3 text-left hover:text-primary">
            <div className="min-w-0">
              <p className="truncate font-medium text-text">{batch.sourceLabel}</p>
              <p className="mt-1 text-xs text-text-muted">{batch.matchCount} matches · {statusLabel(batch.status)} · {formatDate(batch.validatedAt || batch.uploadedAt || batch.createdAt)}</p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0" />
          </button>
        ))}
      </div>
    </section>
  );
}

function UploadStep({
  isUploading,
  onUpload,
}: {
  isUploading: boolean;
  onUpload: (file: File, sourceLabel: string, sourceLedgerId: string) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [sourceLabel, setSourceLabel] = useState('');
  const [sourceLedgerId, setSourceLedgerId] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = async () => {
    if (!file || !sourceLabel.trim() || !sourceLedgerId.trim()) {
      setLocalError('Choose a CSV file and name its source.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setLocalError('Choose a CSV file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLocalError('The CSV file must be 5 MB or smaller.');
      return;
    }
    setLocalError(null);
    await onUpload(file, sourceLabel.trim(), sourceLedgerId.trim());
  };

  return (
    <section aria-labelledby="upload-title">
      <div className="grid gap-8 lg:grid-cols-[1fr_0.72fr]">
        <div>
          <p className="text-sm font-semibold text-primary">Step 1</p>
          <h2 id="upload-title" className="mt-1 text-2xl font-semibold text-text">Upload one match per row</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-text-muted">
            Use player names as they appear in your current record. You will match each name to a squad member before anything is imported.
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5 text-sm font-medium text-text">
              Source name
              <input value={sourceLabel} onChange={(event) => setSourceLabel(event.target.value)} placeholder="Summer league scores" maxLength={120} className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </label>
            <label className="space-y-1.5 text-sm font-medium text-text">
              Source reference
              <input value={sourceLedgerId} onChange={(event) => setSourceLedgerId(event.target.value)} placeholder="summer-league-2026" maxLength={120} className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
              <span className="block text-xs font-normal text-text-muted">Keep this unique for each spreadsheet or season.</span>
            </label>
          </div>

          <label className="mt-5 flex min-h-36 cursor-pointer flex-col items-center justify-center border border-dashed border-border bg-surface-elevated/30 px-5 py-6 text-center hover:border-primary/60">
            <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(event) => setFile(event.target.files?.[0] || null)} />
            <Upload className="h-7 w-7 text-primary" />
            <span className="mt-3 font-semibold text-text">{file ? file.name : 'Choose a CSV file'}</span>
            <span className="mt-1 text-xs text-text-muted">Up to 1,000 matches or 5 MB</span>
          </label>

          {localError ? <p className="mt-3 text-sm text-red-600 dark:text-red-300" role="alert">{localError}</p> : null}
          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={() => void submit()} isLoading={isUploading} leftIcon={<Upload className="h-4 w-4" />}>
              Upload and check
            </Button>
            <Button variant="outline" onClick={downloadTemplate} leftIcon={<Download className="h-4 w-4" />}>
              Download template
            </Button>
          </div>
        </div>

        <aside className="border-l border-border pl-0 lg:pl-7" aria-label="Import requirements">
          <h3 className="flex items-center gap-2 font-semibold text-text"><FileSpreadsheet className="h-5 w-5 text-primary" /> Before you upload</h3>
          <ul className="mt-4 space-y-4 text-sm leading-6 text-text-muted">
            <li><strong className="text-text">One match per row.</strong> Do not create separate winner and loser rows.</li>
            <li><strong className="text-text">Use stable match references.</strong> They prevent the same result from being imported twice.</li>
            <li><strong className="text-text">Keep the template columns.</strong> Optional values can be blank, but column names should not change.</li>
            <li><strong className="text-text">Members join first.</strong> Every player must be an active, verified member of this squad.</li>
          </ul>
        </aside>
      </div>
    </section>
  );
}

function MappingStep({
  detail,
  candidates,
  loadingCandidates,
  isSaving,
  onMap,
  onUnmap,
  onContinue,
}: {
  detail: HistoricalImportBatchDetail;
  candidates: HistoricalImportMemberCandidate[];
  loadingCandidates: boolean;
  isSaving: boolean;
  onMap: (alias: HistoricalImportAlias, userId: number) => Promise<void>;
  onUnmap: (alias: HistoricalImportAlias) => Promise<void>;
  onContinue: () => void;
}) {
  const [query, setQuery] = useState('');
  const aliases = detail.aliases.filter((alias) => alias.displayAlias.toLowerCase().includes(query.trim().toLowerCase()));
  const mappedCount = detail.aliases.filter((alias) => alias.mappedUserId && alias.readiness === 'READY').length;

  return (
    <section aria-labelledby="mapping-title">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-primary">Step 2</p>
          <h2 id="mapping-title" className="mt-1 text-2xl font-semibold text-text">Match player names</h2>
          <p className="mt-2 text-sm text-text-muted">{mappedCount} of {detail.aliases.length} names are ready.</p>
        </div>
        <label className="relative w-full sm:max-w-xs">
          <span className="sr-only">Search uploaded names</span>
          <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-text-muted" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names" className="h-10 w-full rounded-lg border border-border bg-surface pl-9 pr-3 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        </label>
      </div>

      <div className="mt-6 divide-y divide-border border-y border-border" aria-busy={isSaving || loadingCandidates}>
        {aliases.map((alias) => (
          <AliasRow key={alias.id} alias={alias} candidates={candidates} disabled={isSaving || loadingCandidates} onMap={onMap} onUnmap={onUnmap} />
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={onContinue} rightIcon={<ChevronRight className="h-4 w-4" />}>Review matches</Button>
      </div>
    </section>
  );
}

function AliasRow({
  alias,
  candidates,
  disabled,
  onMap,
  onUnmap,
}: {
  alias: HistoricalImportAlias;
  candidates: HistoricalImportMemberCandidate[];
  disabled: boolean;
  onMap: (alias: HistoricalImportAlias, userId: number) => Promise<void>;
  onUnmap: (alias: HistoricalImportAlias) => Promise<void>;
}) {
  return (
    <div className="grid gap-3 py-4 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)_auto] md:items-center">
      <div className="min-w-0">
        <p className="truncate font-semibold text-text">{alias.displayAlias}</p>
        <p className="mt-1 text-xs text-text-muted">Name in uploaded file</p>
      </div>
      <label>
        <span className="sr-only">TARK member for {alias.displayAlias}</span>
        <select
          value={alias.mappedUserId || ''}
          disabled={disabled}
          onChange={(event) => {
            const value = Number(event.target.value);
            if (value) void onMap(alias, value);
          }}
          className="h-11 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
        >
          <option value="">Choose squad member</option>
          {candidates.map((candidate) => (
            <option key={candidate.userId} value={candidate.userId} disabled={!candidate.readyForImport}>
              {candidate.fullName}{candidate.readyForImport ? '' : ' - account setup needed'}
            </option>
          ))}
        </select>
      </label>
      <div className="flex min-w-28 items-center justify-end gap-2">
        {alias.readiness === 'READY' ? (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300"><Check className="h-4 w-4" /> Ready</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 dark:text-amber-300"><AlertCircle className="h-4 w-4" /> Needed</span>
        )}
        {alias.mappedUserId ? (
          <button type="button" disabled={disabled} onClick={() => void onUnmap(alias)} className="rounded-md p-2 text-text-muted hover:bg-surface-elevated hover:text-text" aria-label={`Clear mapping for ${alias.displayAlias}`}>
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ReviewStep({
  detail,
  isValidating,
  onValidate,
  onBack,
  onContinue,
}: {
  detail: HistoricalImportBatchDetail;
  isValidating: boolean;
  onValidate: () => Promise<void>;
  onBack: () => void;
  onContinue: () => void;
}) {
  const [filter, setFilter] = useState<RowFilter>('ALL');
  const summary = detail.summary;
  const hasBlockingRows = summary.needsMappingCount + summary.invalidCount + summary.duplicateCount + summary.conflictCount > 0;
  const filteredRows = detail.rows.filter((row) => {
    if (filter === 'READY') return row.status === 'READY';
    if (filter === 'HISTORY') return row.status === 'HISTORY_ONLY';
    if (filter === 'ATTENTION') return !['READY', 'HISTORY_ONLY'].includes(row.status);
    return true;
  });

  return (
    <section aria-labelledby="review-title">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-primary">Step 3</p>
          <h2 id="review-title" className="mt-1 text-2xl font-semibold text-text">Review matches</h2>
          <p className="mt-2 text-sm text-text-muted">No match is imported from this screen.</p>
        </div>
        <Button variant="outline" onClick={() => void onValidate()} isLoading={isValidating} leftIcon={<RotateCcw className="h-4 w-4" />}>Run checks again</Button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden border border-border bg-border sm:grid-cols-4">
        <Metric label="Ready" value={summary.readyCount} tone="text-emerald-700 dark:text-emerald-300" />
        <Metric label="History only" value={summary.historyOnlyCount} tone="text-sky-700 dark:text-sky-300" />
        <Metric label="Map players" value={summary.needsMappingCount} tone="text-amber-700 dark:text-amber-300" />
        <Metric label="Needs attention" value={summary.invalidCount + summary.duplicateCount + summary.conflictCount} tone="text-red-700 dark:text-red-300" />
      </div>

      <div className="mt-5 flex flex-wrap gap-2" aria-label="Filter matches">
        {(['ALL', 'READY', 'ATTENTION', 'HISTORY'] as const).map((value) => (
          <button key={value} type="button" onClick={() => setFilter(value)} aria-pressed={filter === value} className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${filter === value ? 'border-primary bg-primary text-white' : 'border-border text-text-muted hover:text-text'}`}>
            {{ ALL: 'All', READY: 'Ready', ATTENTION: 'Attention', HISTORY: 'History only' }[value]}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {filteredRows.map((row) => <MatchReviewRow key={row.id} row={row} />)}
        {filteredRows.length === 0 ? <p className="border-y border-border py-8 text-center text-sm text-text-muted">No matches in this view.</p> : null}
      </div>

      {hasBlockingRows ? (
        <div className="mt-5 flex items-start gap-3 border-l-4 border-amber-500 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          Resolve mapping and score issues before the import can be confirmed.
        </div>
      ) : null}

      <div className="mt-6 flex justify-between gap-3">
        <Button variant="outline" onClick={onBack}>Back to mapping</Button>
        <Button onClick={onContinue} disabled={hasBlockingRows} rightIcon={<ChevronRight className="h-4 w-4" />}>Confirm import</Button>
      </div>
    </section>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="bg-surface px-4 py-4">
      <p className={`text-2xl font-bold ${tone}`}>{value}</p>
      <p className="mt-1 text-xs font-semibold uppercase text-text-muted">{label}</p>
    </div>
  );
}

function MatchReviewRow({ row }: { row: HistoricalImportRow }) {
  const status = rowStatus(row);
  return (
    <article className="border border-border bg-surface px-4 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${status.tone}`}>{status.label}</span>
            <span className="text-xs text-text-muted">{row.playedDate} · {row.discipline === 'DOUBLES' ? 'Doubles' : 'Singles'}</span>
          </div>
          <h3 className="mt-3 font-semibold text-text">{row.sideA} <span className="font-normal text-text-muted">vs</span> {row.sideB}</h3>
          <p className="mt-1 text-sm text-text-muted">{row.score} · {row.format.replace(/_/g, ' ').toLowerCase()}</p>
        </div>
        <span className="shrink-0 text-xs text-text-muted">Match {row.sourceMatchId}</span>
      </div>
      {row.reasonCodes.length > 0 ? (
        <ul className="mt-3 space-y-1 border-t border-border pt-3 text-sm text-red-700 dark:text-red-300">
          {row.reasonCodes.map((reason) => <li key={reason}>{reasonLabel(reason)}</li>)}
        </ul>
      ) : null}
    </article>
  );
}

function ConfirmStep({
  detail,
  result,
  isApplying,
  onBack,
  onApply,
}: {
  detail: HistoricalImportBatchDetail;
  result: HistoricalImportApplyResult | null;
  isApplying: boolean;
  onBack: () => void;
  onApply: () => Promise<void>;
}) {
  const summary = detail.summary;
  const [confirmed, setConfirmed] = useState(false);

  if (result) {
    return (
      <section className="mx-auto max-w-3xl py-8 text-center" aria-labelledby="import-complete-title">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
          <Check className="h-6 w-6" />
        </div>
        <h2 id="import-complete-title" className="mt-4 text-2xl font-semibold text-text">Match history imported</h2>
        <p className="mt-2 text-sm text-text-muted">
          {result.importedMatchCount} matches were added. {result.participantNoticeCount} players were notified.
        </p>
        <NavLink to="/play" className="mt-6 inline-flex h-11 items-center justify-center rounded-lg bg-primary px-5 text-sm font-semibold text-white hover:bg-primary/90">
          View Matches
        </NavLink>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl" aria-labelledby="confirm-title">
      <div className="text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"><ShieldCheck className="h-6 w-6" /></div>
        <p className="mt-4 text-sm font-semibold text-primary">Step 4</p>
        <h2 id="confirm-title" className="mt-1 text-2xl font-semibold text-text">Confirm the import</h2>
        <p className="mt-2 text-sm leading-6 text-text-muted">Review the final totals before creating match history for squad members.</p>
      </div>

      <dl className="mt-7 divide-y divide-border border-y border-border">
        <SummaryLine label="Source" value={summary.sourceLabel} />
        <SummaryLine label="Reference" value={summary.sourceLedgerId} />
        <SummaryLine label="Squad timezone" value={summary.squadTimezone} />
        <SummaryLine label="Rating-eligible matches" value={String(summary.readyCount)} />
        <SummaryLine label="History-only matches" value={String(summary.historyOnlyCount)} />
      </dl>

      <label className="mt-6 flex cursor-pointer items-start gap-3 border-y border-border py-4 text-left">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          className="mt-0.5 h-5 w-5 rounded border-border text-primary focus:ring-primary"
        />
        <span>
          <span className="block text-sm font-semibold text-text">I checked the player mapping and scores</span>
          <span className="mt-1 block text-xs leading-5 text-text-muted">Players will see these matches in their history.</span>
        </span>
      </label>

      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        <Button variant="outline" onClick={onBack}>Back to review</Button>
        <Button
          disabled={!detail.canApply || !confirmed || isApplying}
          isLoading={isApplying}
          onClick={() => void onApply()}
          leftIcon={<Check className="h-4 w-4" />}
        >
          Import matches
        </Button>
      </div>
    </section>
  );
}

function SummaryLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-6 py-3 text-sm">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-right font-semibold text-text">{value}</dd>
    </div>
  );
}
